/**
 * Operator 2FA adapters (ADR-0017 slice 5) — the transport seam the shared
 * `TotpAdapter` UI runs on, over the admin `api` client (`/api/admin/auth`).
 *
 * `operatorTotpAdapter` uses the logged-in session (account page); the
 * `operatorSetupAdapter(setupToken)` factory injects a forced-enrollment token
 * as the Bearer (login has no session yet — the password step handed back a
 * `totp_setup` token instead).
 */
import { api } from "./api";
import { portalApi } from "./api-portal";
import type { TotpAdapter, TotpBegin, TotpConfirmed, TotpStatus } from "@/components/auth/totp";

export const operatorTotpAdapter: TotpAdapter = {
  status: () => api.get<TotpStatus>("/auth/2fa"),
  begin: () => api.post<TotpBegin>("/auth/2fa/begin"),
  confirm: (code) => api.post<TotpConfirmed>("/auth/2fa/confirm", { code }),
  disable: (code) => api.post<void>("/auth/2fa/disable", { code }),
};

/** Enroll using a forced-setup token as the Bearer (no session yet). Status /
 *  disable are unreachable in this flow, so they no-op / throw. */
export function operatorSetupAdapter(setupToken: string): TotpAdapter {
  const auth = { headers: { Authorization: `Bearer ${setupToken}` } };
  return {
    status: async () => ({ enabled: false, recovery_codes_remaining: 0 }),
    begin: () => api.post<TotpBegin>("/auth/2fa/begin", undefined, auth),
    confirm: (code) => api.post<TotpConfirmed>("/auth/2fa/confirm", { code }, auth),
    disable: async () => {},
  };
}

// --- portal end-user (app_user) adapters ------------------------------------

export const portalTotpAdapter: TotpAdapter = {
  status: () => portalApi.totpStatus(),
  begin: () => portalApi.totpBegin(),
  confirm: (code) => portalApi.totpConfirm(code),
  disable: (code) => portalApi.totpDisable(code),
};

/** Forced-enrollment over a portal `totp_setup` token (no session yet). */
export function portalSetupAdapter(setupToken: string): TotpAdapter {
  return {
    status: async () => ({ enabled: false, recovery_codes_remaining: 0 }),
    begin: () => portalApi.totpBegin(setupToken),
    confirm: (code) => portalApi.totpConfirm(code, setupToken),
    disable: async () => {},
  };
}
